import type { BillingStatusResponse } from '@hector/shared-types';

/**
 * Les deux arrivées dans l'app qui ouvrent la fenêtre « Bienvenue dans Hector » :
 * le retour de Stripe après paiement, et le démarrage de l'essai sans carte.
 */
export type WelcomeArrival = 'paid' | 'trial';

/** Posé par Stripe (`success_url`) au retour du paiement. */
export const UPGRADE_SUCCESS_PARAM = 'upgrade_success';
/** Posé par `InscriptionPage` quand l'essai de 30 jours vient de démarrer. */
export const TRIAL_STARTED_PARAM = 'trial_started';
/** Paramètres retirés de l'URL à la fermeture, pour que la fenêtre ne revienne pas au rechargement. */
export const WELCOME_PARAMS = [UPGRADE_SUCCESS_PARAM, 'quantity', TRIAL_STARTED_PARAM] as const;

/**
 * Lit l'arrivée dans l'URL. Le paiement l'emporte si les deux paramètres sont
 * présents : un abonnement réglé est l'état le plus avancé des deux.
 */
export function welcomeArrivalFromSearch(params: URLSearchParams): WelcomeArrival | null {
  if (params.get(UPGRADE_SUCCESS_PARAM)) return 'paid';
  if (params.get(TRIAL_STARTED_PARAM)) return 'trial';
  return null;
}

/**
 * Le statut de facturation reflète-t-il déjà l'arrivée ? Juste après le retour de
 * Stripe (ou le démarrage de l'essai), le webhook peut ne pas avoir encore écrit
 * le cache d'abonnement : `quantity` vaut alors 0 ou une valeur d'avant. Tant que
 * ce n'est pas confirmé, aucun nombre ne doit être affiché.
 */
export function isWelcomeStatusConfirmed(
  arrival: WelcomeArrival,
  status: BillingStatusResponse | null | undefined,
): boolean {
  if (!status) return false;
  if (arrival === 'paid') return status.billing_status === 'active';
  return status.billing_status === 'trialing' && status.has_live_subscription;
}

/** « 1 poste » / « N postes ». */
export function seatsLabel(count: number): string {
  return count === 1 ? '1 poste' : `${count} postes`;
}

export interface WelcomeDialogContent {
  /** Phrases du corps, dans l'ordre. */
  paragraphs: string[];
  /**
   * Vrai : bouton principal « Inviter mes collaborateurs » + « Plus tard ».
   * Faux : unique bouton « Continuer ».
   */
  offerInvite: boolean;
}

const INVITE_LABEL = 'Inviter mes collaborateurs';
const LATER_LABEL = 'Plus tard';
const CONTINUE_LABEL = 'Continuer';
export const WELCOME_LABELS = { invite: INVITE_LABEL, later: LATER_LABEL, continue: CONTINUE_LABEL } as const;

/**
 * Texte et boutons de la fenêtre d'accueil, d'après l'arrivée et le statut de
 * facturation (`useBillingStatus`). Fonction pure : c'est elle que les tests
 * unitaires couvrent.
 *
 * L'invitation n'est proposée que si le statut est confirmé ET que l'avocat peut
 * réellement inviter quelqu'un : administrateur, postes payés ou ouverts en plus
 * de ceux déjà occupés, facturation non déléguée à un autre espace (le budget est
 * alors celui du gestionnaire), pas de résiliation programmée, pas de compte
 * exempté (équipe Hector). Mêmes gardes que le bandeau « licences disponibles ».
 */
export function resolveWelcomeDialog(
  arrival: WelcomeArrival,
  status: BillingStatusResponse | null | undefined,
): WelcomeDialogContent {
  const confirmed = status && isWelcomeStatusConfirmed(arrival, status) ? status : null;

  const first =
    arrival === 'paid'
      ? "Votre abonnement est actif. Votre cabinet a désormais accès à l'ensemble d'Hector."
      : trialSentence(confirmed);

  const offerInvite =
    confirmed !== null &&
    confirmed.is_admin &&
    !confirmed.is_exempt &&
    !confirmed.billing_manager &&
    !confirmed.cancel_at_period_end &&
    confirmed.quantity > confirmed.seats_used;

  if (!offerInvite || confirmed === null) return { paragraphs: [first], offerInvite: false };

  return {
    paragraphs: [
      first,
      `Votre cabinet dispose de ${seatsLabel(confirmed.quantity)} : invitez vos collaborateurs pour qu'ils commencent avec vous.`,
    ],
    offerInvite: true,
  };
}

/** La date de fin n'est donnée que lorsqu'elle est connue — jamais devinée côté navigateur. */
function trialSentence(confirmed: BillingStatusResponse | null): string {
  const trialEnd = confirmed ? (confirmed.trial_end ?? confirmed.current_period_end) : null;
  if (!trialEnd) return 'Votre essai de 30 jours a démarré.';
  const date = new Date(trialEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  return `Votre essai de 30 jours a démarré : il court jusqu'au ${date}.`;
}
